"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface Skill {
  name: string;
  level: number;
  note: string;
}

interface SkillCategory {
  id: string;
  name: string;
  description: string;
  iconClass: string;
  skills: Skill[];
}

const categories: SkillCategory[] = [
  {
    id: "frontend",
    name: "Frontend Architecture",
    description: "Building responsive, modern, stateful client environments.",
    iconClass: "fi fi-rr-computer",
    skills: [
      { name: "React 19", level: 95, note: "Concurrent rendering, custom hooks" },
      { name: "Next.js 16", level: 90, note: "App Router, Static Exports" },
      { name: "Vue.js 3", level: 85, note: "Composition API, reactive state" },
      { name: "TailwindCSS 4", level: 95, note: "Premium utility layouts, design tokens" },
      { name: "TypeScript", level: 92, note: "Strict configurations, static checks" },
    ]
  },
  {
    id: "backend",
    name: "Backend Engineering",
    description: "Developing robust microservices and transactional business logic engines.",
    iconClass: "fi fi-sr-network text-xl",
    skills: [
      { name: "Node.js / Express", level: 95, note: "Asynchronous APIs concurrency" },
      { name: "REST APIs", level: 95, note: "Strict serialization schemas, JWTs" },
      { name: "Prisma ORM", level: 90, note: "DB schema mapping, optimizations" },
      { name: "Microservices", level: 85, note: "Decoupled logic pub/sub systems" },
    ]
  },
  {
    id: "database",
    name: "Database Systems",
    description: "Structuring persistent transactions storage and fast in-memory query filters.",
    iconClass: "fi fi-rr-database",
    skills: [
      { name: "MySQL", level: 90, note: "Transactional schemas, custom indexes" },
      { name: "Redis Cache", level: 85, note: "Query caching, token bucket counters" },
    ]
  },
  {
    id: "cloud",
    name: "Cloud Infrastructure",
    description: "Deploying high-performance hosting environments and edge proxy controllers.",
    iconClass: "fi fi-rr-cloud-share",
    skills: [
      { name: "AWS", level: 80, note: "S3, EC2 instances routing configurations" },
      { name: "Cloudflare", level: 90, note: "Edge CDN, WAF, DNS configurations" },
    ]
  },
  {
    id: "devops",
    name: "DevOps & Automation",
    description: "Automating validation gates, container configurations, and release workflows.",
    iconClass: "fi fi-brands-docker",
    skills: [
      { name: "Docker", level: 92, note: "Alpine builds, multi-stage pipelines" },
      { name: "Kubernetes", level: 75, note: "Container clusters scaling routes" },
      { name: "GitHub Actions", level: 90, note: "Lint/Build checks pipelines automation" },
      { name: "Traefik / NGINX", level: 85, note: "Reverse proxies, SSL auto renewal" },
    ]
  },
  {
    id: "architecture",
    name: "Systems Design",
    description: "Modeling scalable pipelines, stateless workers, and decoupled backplane queues.",
    iconClass: "fi fi-rr-chart-tree-map",
    skills: [
      { name: "Load Balancing", level: 90, note: "Anycast routing, upstream routing proxy" },
      { name: "Real-time WebSockets", level: 85, note: "Horizontal coordination via Redis" },
      { name: "CI/CD Pipeline Design", level: 95, note: "Self-healing deployment flow plans" },
    ]
  },
  {
    id: "tools",
    name: "Developer Tooling",
    description: "Enforcing static compliance checkers, version controls, and runtime monitors.",
    iconClass: "fi fi-rr-settings",
    skills: [
      { name: "Linux / NGINX", level: 90, note: "Server configs, firewall filters" },
      { name: "GitHub / Git", level: 95, note: "Monorepo branching, git hooks" },
      { name: "ESLint / Prettier", level: 90, note: "Code formatting validation checks" },
    ]
  },
  {
    id: "ai",
    name: "AI & Integrations",
    description: "Implementing artificial intelligence embeddings and model coordination APIs.",
    iconClass: "fi fi-rr-brain",
    skills: [
      { name: "OpenAI / Claude APIs", level: 85, note: "Prompt optimization, token limits" },
      { name: "Vector Databases", level: 70, note: "Embeddings vector semantic queries" },
    ]
  }
];

export default function Technologies() {
  const [expandedCat, setExpandedCat] = useState<string | null>("frontend");

  const toggleExpand = (catId: string) => {
    setExpandedCat(expandedCat === catId ? null : catId);
  };

  return (
    <section id="technologies" className="section-pad relative z-10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="text-xs font-bold tracking-[0.4em] text-indigo-400 uppercase mb-6 block">
            Capabilities
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-white font-[var(--font-poppins)] tracking-tighter">
            Architectural <span className="gradient-text">DNA</span>
          </h2>
          <p className="mt-6 text-gray-400 max-w-2xl mx-auto text-lg font-medium">
            Categorized engineering stack and capabilities metrics. Tap categories to inspect parameters detail.
          </p>
        </motion.div>

        {/* Skill categories grid layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((cat) => {
            const isExpanded = expandedCat === cat.id;
            return (
              <div
                key={cat.id}
                className={`rounded-3xl border transition-all duration-500 overflow-hidden flex flex-col h-full bg-[#0a061b]/30 ${
                  isExpanded ? "border-indigo-500/40 lg:col-span-2 lg:row-span-2 shadow-[0_0_30px_rgba(99,102,241,0.15)] bg-[#0c0722]/50" : "border-white/5 hover:border-white/20"
                }`}
              >
                {/* Header panel */}
                <button
                  onClick={() => toggleExpand(cat.id)}
                  className="p-6 md:p-8 flex flex-col justify-between items-start text-left w-full h-full relative cursor-pointer"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors duration-300 ${
                      isExpanded ? "bg-indigo-500/20 text-indigo-300" : "bg-white/5 text-gray-400"
                    }`}>
                      <i className={`${cat.iconClass} text-xl`} />
                    </div>
                    <h3 className="text-lg font-black text-white uppercase tracking-wider font-[var(--font-poppins)]">
                      {cat.name}
                    </h3>
                  </div>
                  
                  <p className="text-gray-400 text-xs leading-relaxed font-medium mb-6">
                    {cat.description}
                  </p>

                  <div className="text-[10px] font-black uppercase tracking-widest text-indigo-300 mt-auto flex items-center gap-2">
                    <span>{isExpanded ? "Collapse Details" : "Inspect Metrics"}</span>
                    <i className={`fi ${isExpanded ? "fi-rr-angle-small-up" : "fi-rr-angle-small-down"} text-xs`} />
                  </div>
                </button>

                {/* Expanded skills panel list */}
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-white/10 bg-black/40 px-6 py-6 md:px-8 space-y-4 shrink-0 overflow-hidden"
                    >
                      {cat.skills.map((skill) => (
                        <div key={skill.name} className="space-y-1.5">
                          <div className="flex justify-between items-center text-xs font-mono">
                            <span className="text-white font-bold">{skill.name}</span>
                            <span className="text-indigo-400 font-bold">{skill.level}%</span>
                          </div>
                          
                          {/* Progress bar line */}
                          <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${skill.level}%` }}
                              transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                            />
                          </div>

                          <span className="text-[9px] text-gray-400 font-mono block uppercase">
                            {skill.note}
                          </span>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
